import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import SectionEyebrow from "@/components/site/SectionEyebrow";

const ACUITY = "/book";
const WORKSHOP_IMG = "https://images.squarespace-cdn.com/content/v1/5fd42e869c252757ae0435b2/943f15c8-bbcd-4b08-9d02-af1b69757a92/wbcgboro+doingthework.jpg";
const HERO_IMG = "https://images.squarespace-cdn.com/content/v1/5fd42e869c252757ae0435b2/9c23e3e0-129e-435b-ba7d-e759eab04555/1694719591907.jpg";

const modules = [
    "Foundations of Connection",
    "Identity, Confidence & Presence",
    "Communication With Clarity",
    "Networking for Real People",
    "Building Social Capital",
    "Mentorship & Sponsorship",
    "Conflict Resolution & Hard Conversations",
    "Professional Presence & First Impressions",
    "Execution: Turning Connection Into Outcomes",
];

const workshops = [
    "Networking for Real People",
    "Communicating With Confidence",
    "Building Social Capital",
    "Professional Presence & First Impressions",
    "Relationship-Building for Career Success",
];

const offerings = [
    {
        eyebrow: "Flagship",
        title: "9-Module Curriculum",
        description: "A year-long, structured, interactive system that builds social capital at scale. Grounded in scenario-based learning and culturally responsive teaching, it builds the confidence, communication, and connection skills students need to thrive academically, professionally, and personally.",
        bullets: ["9 structured learning modules", "2–6 live workshops (per package)", "Assessments & reporting", "Faculty support materials", "Optional coaching hours"],
    },
    {
        eyebrow: "Activations",
        title: "High-Impact Workshops",
        description: "Activation points within your connection-centered ecosystem &mdash; high-energy, scenario-based experiences for institutions seeking targeted impact.",
        bullets: workshops,
    },
    {
        eyebrow: "Faculty & Staff",
        title: "Faculty & Staff Training",
        description: "Equipping educators with the tools, language, and relational strategies needed to model and sustain connection-centered culture.",
        bullets: [
            "Strengthen student engagement",
            "Build trust and rapport",
            "Support diverse communication styles",
            "Reinforce the C.A.R.E.&trade; Framework",
        ],
    },
    {
        eyebrow: "On Stage",
        title: "Keynotes & Campus Events",
        description: "High-energy, culturally responsive keynotes and campus events that activate community and set the tone for connection-centered success.",
        bullets: [
            "Orientations & first-year experience events",
            "Leadership programs",
            "Career-readiness initiatives",
            "Student-success summits",
        ],
    },
    {
        eyebrow: "Custom",
        title: "Custom Partnership Packages",
        description: "Multi-year strategy designed for your institution &mdash; integrating C.A.R.E.&trade; across departments with assessment and reporting cycles.",
        bullets: [
            "Multi-year curriculum implementation",
            "Expanded workshop series",
            "Faculty & staff development pathways",
            "Leadership retreats & strategic planning",
        ],
    },
];

export default function Services() {
    return (
        <main className="bg-[#fbf9f8]" data-testid="services-page">
            {/* HERO */}
            <section className="pt-36 pb-24 md:pt-44 md:pb-32">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7 relative z-10">
                        <SectionEyebrow>Services</SectionEyebrow>
                        <h1 className="font-serif text-[48px] sm:text-7xl lg:text-[96px] leading-[0.92] tracking-tight text-[#0c0f3d]">
                            Programs that
                            <span className="block italic text-[#e47a6a]">strengthen success.</span>
                        </h1>
                        <p className="font-sans text-base md:text-lg text-[#0c0f3d]/75 leading-relaxed mt-8 max-w-xl">
                            We don&apos;t deliver sessions &mdash; we architect systems. Our work equips institutions and leadership teams with the frameworks, experiences, and relational infrastructure that make connection inevitable.
                        </p>
                    </div>

                    <div className="lg:col-span-5 relative">
                        <div className="relative aspect-[4/5] overflow-hidden">
                            <img
                                src={HERO_IMG}
                                alt="Shana Price, founder of The Connection Guru"
                                className="w-full h-full object-cover object-top"
                            />
                        </div>
                        <div className="absolute -bottom-6 -left-6 md:-bottom-10 md:-left-10 bg-[#e47a6a] text-[#fbf9f8] px-7 py-6 max-w-[260px]">
                            <p className="font-serif text-2xl md:text-3xl leading-tight italic">We architect systems, not sessions.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* OVERVIEW STRIP */}
            <section className="border-y border-[#e3c7b9] bg-[#e3c7b9]/30 py-12">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-5 gap-6">
                    {["9-Module Curriculum", "Workshops", "Faculty Training", "Keynotes", "Custom Packages"].map((label, i) => (
                        <div key={label} className="text-center md:text-left">
                            <span className="font-sans text-xs text-[#6d91ad] tracking-widest">0{i + 1}</span>
                            <p className="font-serif text-xl md:text-2xl text-[#0c0f3d] mt-1 leading-tight">{label}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* DETAILED OFFERINGS */}
            {offerings.map((o, i) => (
                <section
                    key={o.title}
                    className={`py-20 md:py-28 ${i % 2 === 0 ? "bg-[#fbf9f8]" : "bg-[#0c0f3d] text-[#fbf9f8]"}`}
                    data-testid={`service-section-${i}`}
                >
                    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <SectionEyebrow color={i % 2 === 0 ? "#e47a6a" : "#ffb5ab"}>
                                {o.eyebrow}
                            </SectionEyebrow>
                            <h2
                                className={`font-serif text-4xl md:text-5xl lg:text-6xl leading-[1] tracking-tight ${
                                    i % 2 === 0 ? "text-[#0c0f3d]" : "text-[#fbf9f8]"
                                }`}
                            >
                                {o.title}
                            </h2>
                            <p
                                className={`font-sans text-base md:text-lg leading-relaxed mt-6 ${
                                    i % 2 === 0 ? "text-[#0c0f3d]/75" : "text-[#e3c7b9]/80"
                                }`}
                                dangerouslySetInnerHTML={{ __html: o.description }}
                            />
                        </div>
                        <ul className="lg:col-span-6 lg:col-start-7 space-y-4">
                            {o.bullets.map((b, idx) => (
                                <li
                                    key={idx}
                                    className={`flex items-start gap-4 border-b pb-4 ${
                                        i % 2 === 0 ? "border-[#0c0f3d]/15" : "border-[#6d91ad]/30"
                                    }`}
                                >
                                    <Check
                                        size={18}
                                        className={i % 2 === 0 ? "text-[#e47a6a] mt-1" : "text-[#ffb5ab] mt-1"}
                                    />
                                    <span
                                        className={`font-sans text-base ${
                                            i % 2 === 0 ? "text-[#0c0f3d]/85" : "text-[#e3c7b9]/85"
                                        }`}
                                        dangerouslySetInnerHTML={{ __html: b }}
                                    />
                                </li>
                            ))}
                        </ul>
                    </div>

                    {o.title === "9-Module Curriculum" && (
                        <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-16">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#0c0f3d]/15">
                                {modules.map((m, idx) => (
                                    <div
                                        key={m}
                                        className="bg-[#fbf9f8] p-8 md:p-10"
                                        data-testid={`curriculum-module-${idx}`}
                                    >
                                        <span className="font-serif italic text-[#e47a6a] text-5xl block">
                                            0{idx + 1}
                                        </span>
                                        <h3 className="font-serif text-2xl text-[#0c0f3d] mt-3 leading-tight">
                                            {m}
                                        </h3>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </section>
            ))}

            {/* IMAGERY BREAK */}
            <section className="relative">
                <img src={WORKSHOP_IMG} alt="High-energy workshop" className="w-full aspect-[16/7] object-cover" />
                <div className="absolute inset-0 bg-[#0c0f3d]/55 flex items-center justify-center">
                    <p className="font-serif italic text-3xl md:text-5xl text-[#fbf9f8] text-center px-6 leading-tight max-w-3xl">
                        High-energy, student-centered learning <span className="text-[#ffb5ab]">in action.</span>
                    </p>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="py-24 md:py-36 bg-[#fbf9f8]">
                <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
                    <SectionEyebrow>Ready to Begin</SectionEyebrow>
                    <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1] text-[#0c0f3d]">
                        Not sure which package fits?
                        <span className="block italic text-[#e47a6a]">Let&apos;s talk.</span>
                    </h2>
                    <div className="mt-12 flex flex-wrap gap-4 justify-center">
                        <Link
                            to={ACUITY}
                            data-testid="services-cta-book-btn"
                            className="inline-flex items-center gap-3 px-8 py-4 bg-[#0c0f3d] text-[#fbf9f8] text-xs uppercase tracking-[0.2em] hover:bg-[#e47a6a] transition-colors duration-300"
                        >
                            Book a Consultation
                            <ArrowRight size={14} />
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="services-cta-contact-btn"
                            className="inline-flex items-center gap-3 px-8 py-4 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#0c0f3d] hover:text-[#fbf9f8] transition-colors duration-300"
                        >
                            Submit an Inquiry
                            <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
